<template>
  <div :data-print-id="eventElementId" style="width: 100%">
    <br />
    <AppShowEventPrintBasic :event="event" />
    <section>
      <div class="d-md-flex justify-space-between">
        <h2 class="text-center text-sm-left">
          {{
            $t(
              "people:ShoppingBasket.Events.PersonEventFinancialReport.summary.title",
              "Summary"
            )
          }}
        </h2>
        <div class="text-right">
          <AppPrint :data-element-id="eventElementId" class="ma-2" />
        </div>
      </div>
      <br />
      <StatementReportSummary
        :report="eventFinancialReport"
        :summary-totals="summaryTotals"
      />
    </section>
    <br />

    <section>
      <h2 class="text-center text-sm-left">
        {{
          $t(
            "people:ShoppingBasket.Events.PersonEventFinancialReport.expensesAndIncome.title",
            "Expenses and income"
          )
        }}
      </h2>

      <br />
      <RegistrationDetailsTable
        :registrations="groupedByRegistration"
        :registration-totals="registrationTotals"
      />
    </section>
    <br />

    <section>
      <h2 class="text-center text-sm-left">
        {{
          $t(
            "people:ShoppingBasket.Events.PersonEventFinancialReport.payments.title",
            "Payments"
          )
        }}
      </h2>
      <br />
      <Payments
        :confirmed-payments="eventFinancialReport.confirmedPayments"
        :totals="paymentTotals"
      />
    </section>
    <br />

    <section>
      <h2 class="text-center text-sm-left">
        {{
          $t(
            "people:ShoppingBasket.Events.PersonEventFinancialReport.payouts.title",
            "Payouts"
          )
        }}
      </h2>
      <br />
      <Payouts
        :confirmed-payouts="eventFinancialReport.confirmedPayouts"
        :totals="payoutTotals"
      />
    </section>
  </div>
</template>
<script>
/* eslint-disable vue/no-unused-components */
import gql from "fraql";
import { uniqBy } from "lodash-es";
import { useCurrency } from "@composables/masterdata/config";
import PriceComponentCodenameOrder from "@/utils/priceComponentCodenameOrder";
import AppPrint from "@elements/AppPrint.vue";
import AppShowCountry from "@AppShows/AppShowCountry.vue";

import RegistrationDetailsTable from "./RegistrationDetailsTable.vue";
import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";

import Payments from "./Payments.vue";
import Payouts from "./Payouts.vue";
import StatementReportSummary from "./Summary.vue";

import AppHorseSlot from "@elements/AppHorseSlot.vue";
import AppShowEventPrintBasic from "@AppShows/event/AppShowEventPrintBasic.vue";
import AppShowCompetitionCodeAndName from "@AppShows/event/AppShowCompetitionCodeAndName.vue";

export default {
  name: "PersonStatementReportTemplate",
  components: {
    AppShowCountry,
    AppPrint,
    RegistrationDetailsTable,
    AppHorseSlot,
    Payments,
    Payouts,
    StatementReportSummary,
    // Used in RegistrationDetailsTable
    AppShowPersonName,
    AppShowHorseName,
    AppShowEventPrintBasic,
    AppShowCompetitionCodeAndName
  },
  props: {
    eventFinancialReport: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    }
  },
  setup() {
    return {
      ...useCurrency()
    };
  },
  computed: {
    groupedByRegistration() {
      function clone(x) {
        return JSON.parse(JSON.stringify(x));
      }
      const report = clone(this.eventFinancialReport);

      return uniqBy(
        // Will throw cyclic object without cloning it, due to reference issue (expenseItems references registration that references the same expenseItems)
        [...clone(report.expenseItems), ...clone(report.revenueItems)],
        "registrationId"
      ).map(reg => {
        reg.athletes = reg.registration?.athletes ?? [];
        reg.horses = reg.registration?.horses ?? [];
        delete reg.registration;

        const handleData = (array, type) => {
          const data = array.reduce(
            (accepted, item) => {
              if (item.registrationId == reg.registrationId) {
                delete item.expenseItems;
                delete item.revenueItems;
                delete item.registration;
                item.amountExludingVatCents =
                  item.amountCents - item.vatAmountCents;
                switch (type) {
                  case "revenue":
                    // Turn it into negative number
                    item.amountCents = 0 - item.amountCents;
                    item.amountExludingVatCents =
                      0 - item.amountExludingVatCents;
                    break;
                }

                if (item.entry) {
                  accepted.entries.push(item);
                } else if (item.registrationEventProduct) {
                  accepted.products.push(item);
                } else {
                  accepted.misc.push(item);
                }
              }
              return accepted;
            },
            { entries: [], products: [], misc: [] }
          );

          const rankOrder = PriceComponentCodenameOrder;

          const rankOrderHandler = function (a, b) {
            return rankOrder.findIndex(i => i == a.priceComponentCodename) >
              rankOrder.findIndex(i => i == b.priceComponentCodename)
              ? 1
              : -1;
          };
          return Object.entries(data).flatMap(([key, values]) => {
            switch (key) {
              case "entries":
                return values.sort((a, b) => {
                  const answer = a.entry.competition.code.localeCompare(
                    b.entry.competition.code,
                    this.$federation.language,
                    {
                      numeric: true,
                      sensitivity: "base"
                    }
                  );
                  if (answer == 0) return rankOrderHandler(a, b);
                  return answer;
                });
              case "products":
                return values.sort((a, b) => {
                  const answer =
                    a.registrationEventProduct.eventProduct.name.localeCompare(
                      b.registrationEventProduct.eventProduct.name
                    );
                  if (answer == 0) return rankOrderHandler(a, b);
                  return answer;
                });
              case "misc":
                return values.sort(rankOrderHandler);
            }
          });
        };

        reg.expenseItems = handleData(report.expenseItems, "expense");
        reg.revenueItems = handleData(report.revenueItems, "revenue");

        const { subTotalVatCents, subTotalCents } = [
          ...reg.expenseItems,
          ...reg.revenueItems
        ].reduce(
          (total, item) => {
            total.subTotalVatCents += item.vatAmountCents;
            total.subTotalCents += item.amountCents;
            return total;
          },
          { subTotalVatCents: 0, subTotalCents: 0 }
        );

        reg.subTotalVatCents = subTotalVatCents;
        reg.subTotalCents = subTotalCents;

        reg.subTotalExpenses = reg.expenseItems.reduce(
          (a, exp) => (a += exp.amountCents),
          0
        );
        reg.subTotalRevenues = reg.revenueItems.reduce(
          (a, rev) => (a += rev.amountCents),
          0
        );
        return reg;
      });
    },
    registrationTotals: ({ groupedByRegistration: regs }) => {
      return regs.reduce(
        (total, reg) => {
          total.totalAmountToPay += reg.subTotalExpenses + reg.subTotalRevenues;

          total.totalIncome += reg.subTotalRevenues;
          total.totalExpenses += reg.subTotalExpenses;
          total.totalVATCents += reg.subTotalVatCents;
          return total;
        },
        {
          totalVATCents: 0,
          totalAmountToPay: 0,
          totalIncome: 0,
          totalExpenses: 0
        }
      );
    },
    paymentTotals: ({ eventFinancialReport: report }) => {
      return {
        totalPaidCents: report.confirmedPayments.reduce(
          (total, { amountCents }) => (total += amountCents),
          0
        ),
        totalPayableAmountCents: report.totalPayableAmountCents
      };
    },
    payoutTotals: ({ eventFinancialReport: report }) => {
      return {
        totalReceivedCents: report.confirmedPayouts.reduce(
          (total, { amountCents }) => (total += amountCents),
          0
        )
      };
    },
    summaryTotals: ({ eventFinancialReport: report }) => {
      return (
        0 -
        report.totalPayoutsAmountCents +
        report.totalConfirmedPaymentsAmountCents +
        report.totalPayableAmountCents +
        (0 - report.totalPendingReturnsAmountCents) +
        report.pendingManualCreditTransferPayouts.reduce(
          (t, pen) => (t -= pen.amountCents),
          0
        )
      );
    },
    eventElementId: ({ event }) => event.id.split("-").join("") + "-element"
  },
  fragments: {
    competitionFields: gql`
      fragment _ on Competition {
        id
        ${AppShowCompetitionCodeAndName.fragments.competitionFields}
      }
    `,
    eventFields: gql`
      fragment _ on Event {
        id
        ${AppShowEventPrintBasic.fragments.event}
      }
    `,
    person: AppShowPersonName.fragments.person,
    horse: gql` 
      fragment _ on Horse {
        ${AppShowHorseName.fragments.horse}
        ${AppHorseSlot.fragments.horseFields}
      }
    `
  }
};
</script>
